import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    Inject,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    SimpleChanges,
    ViewChild
} from "@angular/core";
import {EntityData, EntityDataStatus, SearchResult} from "@fedeltapointofsale/data-client";
import {Subscription} from "rxjs";
import {FormGroup, NG_VALUE_ACCESSOR} from "@angular/forms";
import {
    FUIOptionMetaData,
    FUISelectAction,
    FUISelectControlComponent,
    FUISelectNavComponent,
    FUIStackedPageService
} from "@fedeltapointofsale/ui";
import {ExtendedSearchParams, SearchType} from "../search/search-page/extended-search-params";
import {
    AbstractSelectControlComponent,
    SelectControlMode,
    SelectControlSelector
} from "./abstract-select-control.component";
import {EDIT_GRID_ADD_ROW_CONTEXT} from "../edit/edit-grid/edit-grid-add-row/edit-grid-add-row.component";
import {EditParams} from "../edit/edit-page/edit-params";
import {EditMode} from "../edit/edit-page/edit-mode";
import {AnalyticsService} from "../../../analytics/analytics.service";
import {AnalyticEvent} from "../../../analytics/analytic-event";
import {SearchControlSelectObjects} from "./search-control-select-objects";
import {TranslateService} from "@ngx-translate/core";

const CUSTOM_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SingleSelectControlComponent),
    multi: true
};

@Component({
    selector: "wbo-single-select-control",
    templateUrl: "single-select-control.component.html",
    providers: [
        CUSTOM_VALUE_ACCESSOR,
        {
            provide: SelectControlSelector,
            useExisting: forwardRef(() => SingleSelectControlComponent),
            multi: true
        }
    ]
})
export class SingleSelectControlComponent extends AbstractSelectControlComponent implements OnDestroy, OnChanges, OnInit, AfterViewInit {
    SelectControlMode = SelectControlMode;

    /**
     * It is used when the entity that is being searched is already in the main entity. For example: Primary category. As
     * the source can be a searchModel and it will load only a number of records, the selected value may not be found in this list to get the selected entity.
     * So it will use this value to set the selected record and also when another item is selected it assigns its values to this objectValue.
     */
    @Input() objectValue: EntityData<any> | FormGroup;

    @Input() public isNumeric: boolean = false;

    /**
     * Defines the label for the option to clear the selection
     */
    @Input() clearSelectionOptionLabel: string;
    @Input() allowClearSelectionOption: boolean = true;

    @Input() hideExtendedSearchOption: boolean = false;
    @Input() showAddOption: boolean = false;
    @Input() hideAddOption: boolean = false;
    @Input() overrideOpenView: boolean = false;
    @Input() allowDirectInput: boolean = false;
    @Input() actions: FUISelectAction[];

    /**
     * Defines an initial default value. It will not trigger the valueChange unless notifyInitialDefault is specified.
     */
    @Input() initialDefaultValue: any;
    @Input() notifyInitialDefault: boolean = false;

    @Output() onSelect: EventEmitter<any> = new EventEmitter();
    @Output() onAdd: EventEmitter<boolean> = new EventEmitter();
    @Output() onAction: EventEmitter<FUISelectAction> = new EventEmitter<FUISelectAction>();
    @Output() onClear: EventEmitter<any> = new EventEmitter();

    @ViewChild(FUISelectControlComponent) selectControlComponent: FUISelectControlComponent;
    @ViewChild(FUISelectNavComponent) selectNavComponent: FUISelectNavComponent;

    private stackedViewSubscription: Subscription;
    private stackedViewAddSubscription: Subscription;

    private _selectControlValue: any;
    private directInputChanged: boolean = false;

    constructor(injector: Injector,
                changeDetector: ChangeDetectorRef,
                translateService: TranslateService,
                private stackedPageService: FUIStackedPageService,
                @Optional() @Inject(EDIT_GRID_ADD_ROW_CONTEXT) private editGridAddRowContext: boolean,
                private analyticsService: AnalyticsService) {
        super(changeDetector, translateService);
    }

    @Input() getOptionMetaData: (option: any) => FUIOptionMetaData = () => undefined;

    get selectControlValue(): any {
        return this._selectControlValue;
    }

    set selectControlValue(value: any) {
        this._selectControlValue = value;
        if (value == undefined) {
            const oldValue: any = this.value;
            this.value = value; // This must happen before resetting the form control or else the value change event will not fire
            if (oldValue != undefined) {
                this.clearHandler();
            }
        } else if (typeof value !== "object") {
            if (this.value !== value) {
                this.value = value;
                this.objectValue = undefined;
            }
        } else if (value[this.valueField] !== this.value) {
            this.value = value[this.valueField];
            // set object value
            if (this.objectValue) {
                if (this.objectValue instanceof EntityData) {
                    this.objectValue.assign(value, EntityDataStatus.LOADED);
                } else if (this.objectValue instanceof FormGroup) {
                    (<FormGroup>this.objectValue).patchValue(value);
                }
            }

            this.onSelect.emit(value);
        }
    }

    /**
     * Only used for handling direct input of avlue
     * @param value
     */
    selectControlChanged(value: any): void {
        if (this.allowDirectInput && typeof value !== "object") {
            this.directInputChanged = true;
            this.selectControlValue = value;
        }
    }

    override ngOnInit(): void {
        if (this.editGridAddRowContext) {
            this.mode = SelectControlMode.Autocomplete;
        }
        super.ngOnInit();
    }

    ngAfterViewInit(): void {
        if (this.initialDefaultValue && this._value == undefined) {
            if (this.notifyInitialDefault) {
                this.value = this.initialDefaultValue;
            } else {
                this._value = this.initialDefaultValue;
            }
            this.evaluateValueChange();
        }
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.stackedViewSubscription) {
            this.stackedViewSubscription.unsubscribe();
        }
        if (this.stackedViewAddSubscription) {
            this.stackedViewAddSubscription.unsubscribe();
        }
    }

    openExtendedSearch(): void {
        if (this.stackedViewSubscription) {
            this.stackedViewSubscription.unsubscribe();
        }
        this.stackedViewSubscription = this.stackedPageService.open(this.extendedSearchComponent,
            new ExtendedSearchParams(SearchType.SINGLE_SELECTION, this.extendedSearchComponentParams)).subscribe(results => {
            if (results.length) {
                this.loadDataFromSearchModel();
                this.selectControlValue = results[0];
                this.changeDetector.detectChanges();
                if (this.selectControlComponent) {
                    this.selectControlComponent.close();
                } else if (this.selectNavComponent) {
                    this.selectNavComponent.close();
                }
            }
        });

        this.analyticsService.trackEvent(AnalyticEvent.ENTITY_INTERACTION_SELECT_EXTENDED_SEARCH, this.sourceSearchModel);
    }

    public openAdd(extendedEditComponent: any = this.extendedEditComponent, extendedEditComponentParams: any = this.extendedEditComponentParams): void {
        if (extendedEditComponent) {
            if (this.stackedViewAddSubscription) {
                this.stackedViewAddSubscription.unsubscribe();
            }
            this.stackedViewAddSubscription = this.stackedPageService.open(extendedEditComponent, new EditParams(undefined, EditMode.ADD, null, extendedEditComponentParams)).subscribe((entity: EntityData<any>) => {
                this.loadDataFromSearchModel();
                this.selectControlValue = entity;
                this.changeDetector.detectChanges();
                if (this.selectControlComponent) {
                    this.selectControlComponent.close();
                } else if (this.selectNavComponent) {
                    this.selectNavComponent.close();
                }
            });
        } else {
            this.onAdd.emit(true);
        }

        this.analyticsService.trackEvent(AnalyticEvent.ENTITY_INTERACTION_SELECT_ADD_NEW, this.sourceSearchModel);
    }

    public openView(option: any, allowOverride: boolean = false, extendedEditComponent: any = this.extendedEditComponent): void {
        if (this.overrideOpenView && allowOverride) {
            this.onOptionOpen.emit(option);
        } else if (extendedEditComponent) {
            this.stackedPageService.open(extendedEditComponent, new EditParams(this.sourceSearchModel.getIdentifier(option), EditMode.VIEW));
        } else if (this.showOptionOpenOption) {
            this.onOptionOpen.emit(option);
        } else {
            this.onSelect.emit(option);
        }

        this.analyticsService.trackEvent(AnalyticEvent.ENTITY_INTERACTION_SELECT_VIEW_EXISTING, this.sourceSearchModel);
    }

    clearHandler(): void {
        this.onClear.emit();
        if (this.objectValue) {
            if (this.objectValue instanceof EntityData) {
                this.objectValue.removeFromParent();
            } else if (this.objectValue instanceof FormGroup) {
                (<FormGroup>this.objectValue).reset();
            }
        }
    }

    click() {
        if (this.selectControlComponent) {
            this.selectControlComponent.click();
        } else if (this.selectNavComponent) {
            this.selectNavComponent.click();
        }
    }

    focus() {
        if (this.selectControlComponent) {
            this.selectControlComponent.focus();
        }
    }

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        if (changes["value"] || changes["objectValue"] || changes["data"]) {
            this.evaluateValueChange(!!changes["value"]);
        }
    }

    evaluateValueChange(valueChanged: boolean = true): void {
        if (this.value == undefined) {
            this.selectControlValue = this.value;
        } else if (!this._selectControlValue || !valueChanged || this._selectControlValue[this.valueField] !== this.value) {
            if (this.objectValue && this.objectValue[this.valueField] === this.value) {
                this._selectControlValue = this.objectValue;
            } else if (!this.allowDirectInput || !this.isFocussed || typeof this.value === "object") {
                if (this.sourceSearchModel) {
                    const searchFilter: any = {...this.sourceSearchModel.filter};
                    searchFilter[this.valueSearchField || this.valueField] = this.value;
                    this.subscriptions.add(this.sourceSearchModel.searchRecords(searchFilter, undefined, 2, undefined).subscribe((searchResult: SearchResult) => {
                        if (this.value != undefined) {
                            if (searchResult.data.length === 1) {
                                this._selectControlValue = searchResult.data[0];
                                try {
                                    this.changeDetector.detectChanges();
                                } catch (e) {
                                }
                            } else if (this.allowDirectInput) {
                                this._selectControlValue = this.value;
                                this.changeDetector.detectChanges();
                            } else {
                                console.log("Failed to find the selected record for searchModel " + this.sourceSearchModel.getResourceName() + " with search field " + this.value + ". Verify that the search field '" + this.valueField + "' is included in the filters.");
                                this._selectControlValue = {}
                                this._selectControlValue[this.valueField] = this.value;
                            }
                        }
                    }, () => {
                        // @todo needs to be dealt with
                    }));
                } else if (this.data) {
                    const record: any = this.data.find(value => value[this.valueField] === this.value);
                    if (record) {
                        this._selectControlValue = record;
                        this.changeDetector.detectChanges();
                    } else if (this.allowDirectInput) {
                        this._selectControlValue = this.value;
                        this.changeDetector.detectChanges();
                    }
                }
            }
        }
    }

    override onFocus(focussed: boolean): void {
        super.onFocus(focussed);
        if (!focussed && this.allowDirectInput && this.directInputChanged) {
            this.directInputChanged = false;
            this.evaluateValueChange(false);
        }
    }

    select(value: any): void {
        this.directInputChanged = false;
        this.selectControlValue = value;
    }

    override writeValue(obj: any): void {
        super.writeValue(obj);
        this.evaluateValueChange();
    }

    override setSearchControlSelectObjects(searchControlSelectObjects: SearchControlSelectObjects): void {
        setTimeout(() => {
            if (this.valueField) {
                const values = [];
                searchControlSelectObjects.objects.forEach(value => {
                    const selectObject = {};
                    selectObject[this.valueField] = value.id;
                    selectObject[this.labelField] = value.name;
                    values.push(selectObject);
                });
                if (values.length) {
                    this.selectControlValue = values[0];
                }
            }
        });
    }
}
